import { useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';
import { withImageOptimizationQuery } from '@common/utils';
import { AdBanner } from '@widgets/ad';
import { queryOptions, useCatalogProductIdParams, usePdpBaseInfo } from '@domains/pdp-common';

import { getBannerAdList } from '../graphql/banner-ad-list.2';
import { GetBannerAdList_pdp_banner_ad_creative_list_item_list } from '../graphql/banner-ad-list_types.2';

const GET_BANNER_AD_LIST_QUERY_KEY = 'getBannerAdList';

export type BannerType = 'BAR_AUTO' | 'PRODUCT_CARD';

interface Params {
  shop_category_id: string;
  shop_id?: string;
}

export function getBannerAdListQueryOption(params: Params, options?: GqlRequestOptions) {
  const variables = {
    ...params,
    is_first_page: true,
    banner_type_list: ['BAR_AUTO', 'PRODUCT_CARD'],
  };

  return {
    queryKey: [GET_BANNER_AD_LIST_QUERY_KEY, variables],
    queryFn: async function () {
      const { data } = await getBannerAdList(variables, { ...options, show_alert: false });
      return data.pdp_banner_ad_creative_list;
    },
    ...queryOptions,
  };
}

export function useBannerAdListQuery(options?: GqlRequestOptions) {
  const param = useCatalogProductIdParams();
  const { shop } = usePdpBaseInfo(param);
  const { data, isLoading, ...rest } = useQuery(
    getBannerAdListQueryOption(
      {
        shop_category_id: `${shop.trait.category}`,
        shop_id: shop.id,
      },
      options,
    ),
  );

  const ad_banner_items = data?.item_list?.map(convertToAdBanner) ?? [];
  const bar_auto_type_ad_banner_items = ad_banner_items.filter((item) => item.type === 'BAR_AUTO');
  const product_card_type_ad_banner_items = ad_banner_items.filter((item) => item.type === 'PRODUCT_CARD');

  return {
    ...rest,
    ad_banner_items,
    bar_auto_type_ad_banner_items,
    product_card_type_ad_banner_items,
    is_loading: isLoading,
  };
}

const convertToAdBanner = (
  item: GetBannerAdList_pdp_banner_ad_creative_list_item_list,
  banner_index: number,
): AdBanner => {
  return {
    type: item.banner_info.banner_type as AdBanner['type'],
    shop_id: item.banner_info.shop_id,
    shop_name: item.banner_info.shop_name ?? '',
    ad_id: item.creative?.ad_id ?? '',
    creative_id: item.creative?.creative_id ?? '',
    title: item.creative?.title ?? '',
    sub_title: item.creative?.sub_title ?? undefined,
    background_color: item.banner_info?.background_color ?? '',
    button_color: item.banner_info.color_set_info?.button_color ?? undefined,
    text_color: item.banner_info.color_set_info?.text_color ?? undefined,
    banner_image_url:
      withImageOptimizationQuery(item.creative?.image_url, { format: 'webp', quality: 80 }) ??
      item.creative?.image_url ??
      '',
    landing_url: item.creative?.landing_url ?? '',
    log: item.log ?? undefined,
    banner_index,
  };
};
